import React, { PropsWithChildren, useCallback, useContext, useEffect, useMemo, useState } from 'react'

import { Availability } from '../data/use-clinician'
import { cart } from '../lib/cart'

export type Symptom = { question: string; answer: string }
export type Summary = { content: string; updatedAt: string }

export interface CartProduct {
  productId: number
  patientId?: number
  clinicianId?: number
  symptoms?: Symptom[]
  summary?: Summary
  schedule?: Availability
  createdAt?: number
  workflowId?: string
  lockedAt?: number
}

interface CartState {
  product: CartProduct | null
  sessionKey?: string
}

interface ContextType extends CartState {
  addProductToCart: (product: CartProduct, saveInMemory?: boolean) => Promise<string | undefined>
  removeProductFromCart: () => void
  updateProductToCart: (product: Partial<CartProduct>, saveInMemory?: boolean) => Promise<string | undefined>
}

export const CartContext = React.createContext<ContextType | undefined>(undefined)

const initialState: CartState = {
  product: null,
}

export const CartProvider: React.FC<PropsWithChildren> = ({ children }) => {
  const [cartState, setCartState] = useState<CartState>(initialState)
  const [sessionKey, setSessionKey] = useState<string>()

  useEffect(() => {
    cart.getSessionKey().then(key => {
      if (key) setSessionKey(key)
    })
  }, [])

  useEffect(() => {
    if (!sessionKey) {
      return
    }

    cart.getFromMemory().then(product => {
      setCartState({ product })
    })
  }, [sessionKey])

  const addProductToCart = useCallback(
    async (product: CartProduct, saveInMemory = true): Promise<string | undefined> => {
      setCartState({ product })

      if (saveInMemory) {
        const key = await cart.saveInMemory(product)
        setSessionKey(key)

        return key
      }
    },
    []
  )

  const updateProductToCart = useCallback(
    async (product: Partial<CartProduct>, saveInMemory = true): Promise<string | undefined> => {
      if (!cartState.product) return

      setCartState({ product: { ...cartState.product, ...product } })

      if (sessionKey && saveInMemory) {
        return await cart.update(sessionKey, product)
      }
    },
    [cartState.product, sessionKey]
  )

  const removeProductFromCart = useCallback(() => {
    setCartState({ product: null })
    return cart.deleteSessionKey()
  }, [])

  const context = useMemo<ContextType>(
    () => ({
      addProductToCart,
      removeProductFromCart,
      updateProductToCart,
      ...cartState,
      sessionKey,
    }),
    [addProductToCart, removeProductFromCart, updateProductToCart, cartState, sessionKey]
  )

  return <CartContext.Provider value={context}>{children}</CartContext.Provider>
}

export const useSessionCart = () => {
  const context = useContext(CartContext)

  if (!context) {
    throw new Error('useCart() must be wrapped inside CartProvider.')
  }

  return context
}
